import { inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { OnboardingContextService } from '@rma/dashboard/account-setup/public-api/onboarding-service';
import { DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { UploadedImage } from '@rma/dashboard/generic/orc/image-upload';
import { IntercomCustomEventType, RmaIntercomService } from '@rma/dashboard/generic/orc/intercom';
import { BrandKitDisplay, BrandKitsContextService } from '@rma/dashboard/settings/public/api-brand-kits';
import { ProfileType } from '@rma/generic/domain/types';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  ReplaySubject,
  skip,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DEFAULT_PROFILE_BRANDING } from '../constants/default-branding';
import { ProfileBranding } from '../models/branding.models';
import { BrandingForProfile } from '../models/branding-for-profile';
import { getImageConfigByImageType, ProfileThemeImageType } from '../models/theme-image-params';
import { ThemeParams } from '../models/theme-params';
import { getBrandKitLinkMetadata } from '../util/brand-kit-link-metadata';
import { ProfileThemeContextService } from './profile-theme-context.service';

@Injectable({ providedIn: 'root' })
export class ProfileBrandingService {
  private readonly intercomService = inject(RmaIntercomService);
  private readonly onboardingService = inject(OnboardingContextService);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly profileThemeContextService = inject(ProfileThemeContextService);
  private readonly brandKitsCtx = inject(BrandKitsContextService);

  private readonly context$ = this.profileContextService.profileContext$;

  private readonly refresh = new Subject<void>();
  private readonly brandingForProfile = new ReplaySubject<BrandingForProfile>(1);

  public readonly profileImage$: Observable<string>;
  public readonly profileUrl$: Observable<string>;
  public readonly branding$: Observable<ProfileBranding>;
  public readonly selectedBrandKitId: Signal<number | undefined>;
  public readonly brandKits$: Observable<BrandKitDisplay[]>;

  private refreshedForProfile?: string;

  public constructor() {
    this.profileImage$ = this.context$.pipe(map(({ profileImage }) => profileImage));
    this.profileUrl$ = this.context$.pipe(map(({ profileUrl }) => profileUrl));

    // Merged, not switched: the refresh arm's error handling must not outlive its own response, or a later
    // profile's failed fetch is dropped too and the previous profile's branding stays on screen.
    this.branding$ = merge(this.profileThemeContextService.getBranding(), this.refresh.pipe(switchMap(() => this.refetchBranding()))).pipe(
      rmaShareReplay(),
    );

    // Stamp on arrival, not on read: the context is only guaranteed to be the payload's own at the moment it
    // emits, so reading it later would pair a replayed payload with a profile the user has since moved to.
    this.branding$
      .pipe(withLatestFrom(this.context$), takeUntilDestroyed())
      .subscribe(([branding, context]) => this.brandingForProfile.next({ branding, profileKey: this.getProfileKey(context) }));

    this.selectedBrandKitId = toSignal(this.branding$.pipe(map(({ brandKitId }) => brandKitId ?? undefined)));
    // GetBrandKits heals a profile that owns no kit, server-side and silently, so the cached branding is stale from here.
    this.brandKits$ = this.brandKitsCtx.getBrandKits().pipe(
      tap(() => this.refreshBrandingIfUnlinked()),
      rmaShareReplay(),
    );
  }

  public removeImageFromTheme(imageType: ProfileThemeImageType): Observable<boolean> {
    return this.getImageConfigByMode(imageType).pipe(
      switchMap((uploadParams) => this.profileThemeContextService.deleteImage(uploadParams.imageType)),
      map(() => true),
      tap(() => {
        this.logProfileBrandingChange({ [`${imageType}HasValue`]: false });
        this.refreshBranding();
      }),
    );
  }

  public addImageToTheme(type: ProfileThemeImageType, image: UploadedImage, isOriginal: boolean): Observable<UploadedImage> {
    return this.getImageConfigByMode(type).pipe(
      switchMap((imageConfig) => this.profileThemeContextService.uploadImage(imageConfig.imageType, image.ImageName, isOriginal)),
      map(() => image),
      tap(() => {
        const original = isOriginal ? { [`${type}OriginalHasValue`]: !!image.ImageUrl } : {};
        this.logProfileBrandingChange({ ...original, [`${type}HasValue`]: !!image.ImageUrl });
        this.refreshBranding();
      }),
      tap(() => this.onboardingService.markTaskCompleted('AddBranding')),
    );
  }

  public getParamsConfigByImageType(imageType: ProfileThemeImageType, profileType: ProfileType): ThemeParams {
    return getImageConfigByImageType(imageType, profileType);
  }

  public refreshBranding(): void {
    this.refresh.next();
  }

  public linkBrandKit(id: number): Observable<void> {
    return this.profileThemeContextService.updateBrandKit(id).pipe(
      tap(() => {
        this.refreshBranding();
        this.logBrandKitLinked(id);
      }),
    );
  }

  private logBrandKitLinked(id: number): void {
    this.brandKits$
      .pipe(
        map((brandKits) => brandKits.find((kit) => kit.id === id)),
        take(1),
      )
      .subscribe((brandKit) => {
        if (brandKit) {
          this.logProfileBrandingChange(getBrandKitLinkMetadata(brandKit));
        }
      });
  }

  private refreshBrandingIfUnlinked(): void {
    this.brandingForProfile
      .pipe(
        withLatestFrom(this.context$),
        // Brand kits arrive before the branding on a cold load, so wait for this profile's own payload.
        filter(([{ profileKey }, context]) => profileKey === this.getProfileKey(context)),
        take(1),
        // Keyed on profile identity, not context emissions: the context re-emits for the same profile whenever
        // anything calls refreshProfileContext(), and brand kits do not follow, so aborting on those would
        // strand the wait — on the very emission carrying the branding it is waiting for.
        takeUntil(this.profileChanged$()),
      )
      .subscribe(([{ branding, profileKey }]) => {
        if (branding.brandKitId != null) {
          return;
        }

        if (this.refreshedForProfile === profileKey) {
          return;
        }

        this.refreshedForProfile = profileKey;
        this.refreshBranding();
      });
  }

  private refetchBranding(): Observable<ProfileBranding> {
    // One response, for the profile that asked for it. getBranding() follows the profile identifier, so
    // without the takeUntil a switch mid-flight would refetch the new profile's branding the base stream is
    // already fetching. The default is what a failed fetch looks like here — the context service swallows
    // HTTP errors into it — and dropping it leaves the cached branding in place.
    return this.profileThemeContextService.getBranding().pipe(
      takeUntil(this.profileChanged$()),
      take(1),
      filter((branding) => branding !== DEFAULT_PROFILE_BRANDING),
      catchError(() => EMPTY),
    );
  }

  private profileChanged$(): Observable<string> {
    return this.context$.pipe(
      map((context) => this.getProfileKey(context)),
      distinctUntilChanged(),
      skip(1),
    );
  }

  private getProfileKey({ profileType, profileCode }: { profileType: ProfileType; profileCode: string }): string {
    // profileCode alone collides across profile types, so it cannot identify a profile on its own.
    return `${profileType}/${profileCode}`;
  }

  private getImageConfigByMode(type: ProfileThemeImageType): Observable<ThemeParams> {
    return this.profileThemeContextService.imageConfigurations$.pipe(map((configs) => configs[type]));
  }

  private logProfileBrandingChange(metadata: Record<string, boolean>): void {
    this.intercomService.trackEvent(IntercomCustomEventType.ProfileBrandingSet, metadata);
  }
}
